import { BitcoinNetworkType, MessageSigningProtocols, AddressPurpose as SatsConnectAddressPurpose, getProviderById, getProviders } from 'sats-connect';
import { CoreHelperUtil } from '@reown/appkit-controllers';
import { PresetsUtil } from '@reown/appkit-utils';
import { mapSatsConnectAddressPurpose } from '../utils/BitcoinConnector.js';
import { AddressPurpose } from '../utils/BitcoinConnector.js';
import { mapCaipNetworkToXverseName, mapXverseNameToCaipNetwork } from '../utils/HelperUtil.js';
import { ProviderEventEmitter } from '../utils/ProviderEventEmitter.js';
export class SatsConnectConnector extends ProviderEventEmitter {
    constructor({ provider, requestedChains, getActiveNetwork }) {
        super();
        this.chain = 'bip122';
        this.type = 'ANNOUNCED';
        this.requestedChains = [];
        this.walletUnsubscribes = [];
        this.wallet = provider;
        this.requestedChains = requestedChains;
        this.provider = this;
        this.getActiveNetwork = getActiveNetwork;
    }
    get id() {
        return this.name;
    }
    get name() {
        return this.wallet.name;
    }
    get explorerId() {
        return PresetsUtil.ConnectorExplorerIds[this.wallet.name];
    }
    get imageUrl() {
        return this.wallet.icon;
    }
    get chains() {
        return this.requestedChains.filter(chain => chain.chainNamespace === 'bip122');
    }
    async request(args) {
        return this.internalRequest(args.method, args.params);
    }
    async connect() {
        const address = await this.getAccountAddresses()
            .then(addresses => addresses[0]?.address)
            .catch(() => this.internalRequest('wallet_connect', null).then(response => response?.addresses
            .map(add => ({ ...add, purpose: mapSatsConnectAddressPurpose(add.purpose) }))
            .find(add => add.purpose === AddressPurpose.Payment)?.address));
        if (!address) {
            throw new Error('No address available');
        }
        this.bindEvents();
        this.emit('accountsChanged', [address]);
        return address;
    }
    async disconnect() {
        await this.internalRequest('wallet_disconnect', null);
        this.unbindEvents();
    }
    async getAccountAddresses() {
        const response = await this.internalRequest('getAddresses', {
            purposes: [
                SatsConnectAddressPurpose.Payment,
                SatsConnectAddressPurpose.Ordinals,
                SatsConnectAddressPurpose.Stacks
            ],
            message: 'Connect to your wallet'
        });
        if (response.addresses.length === 0) {
            throw new Error('No address available');
        }
        return response.addresses.map(address => ({
            ...address,
            purpose: mapSatsConnectAddressPurpose(address.purpose)
        }));
    }
    static getWallets({ requestedChains, getActiveNetwork }) {
        if (!CoreHelperUtil.isClient()) {
            return [];
        }
        const providers = getProviders();
        return providers.map(provider => new SatsConnectConnector({ provider, requestedChains, getActiveNetwork }));
    }
    async signMessage(params) {
        const protocol = params.protocol?.toUpperCase();
        const res = await this.internalRequest('signMessage', { ...params, protocol });
        return res.signature;
    }
    async signPSBT(params) {
        const signInputs = params.signInputs.reduce((acc, input) => {
            const currentIndexes = acc[input.address] || [];
            currentIndexes.push(input.index);
            return { ...acc, [input.address]: currentIndexes };
        }, {});
        const res = await this.internalRequest('signPsbt', {
            psbt: params.psbt,
            broadcast: params.broadcast,
            signInputs
        });
        return res;
    }
    async switchNetwork(caipNetworkId) {
        const networkName = mapCaipNetworkToXverseName(caipNetworkId);
        await this.internalRequest('wallet_changeNetwork', { name: networkName });
    }
    async sendTransfer({ amount, recipient }) {
        const parsedAmount = Number(amount);
        if (isNaN(parsedAmount)) {
            throw new Error('Invalid amount');
        }
        const res = await this.internalRequest('sendTransfer', {
            recipients: [{ address: recipient, amount: parsedAmount }]
        });
        return res.txid;
    }
    getWalletProvider() {
        return getProviderById(this.wallet.id);
    }
    async internalRequest(method, options) {
        const response = await this.getWalletProvider()
            .request(method, options)
            .catch(error => {
            if ('jsonrpc' in error && 'error' in error) {
                return error;
            }
            throw error;
        });
        if ('result' in response) {
            return response.result;
        }
        throw { ...response.error, name: 'RPCError' };
    }
    bindEvents() {
        this.unbindEvents();
        const provider = this.getWalletProvider();
        if (typeof provider.addListener !== 'function') {
            console.warn(`SatsConnectConnector:bindEvents - wallet provider "${this.name}" does not support events`);
            return;
        }
        this.walletUnsubscribes.push(provider.addListener('accountChange', async (_data) => {
            const address = await this.connect();
            this.emit('accountsChanged', [address]);
        }), provider.addListener('disconnect', _data => {
            this.emit('disconnect');
        }), provider.addListener('networkChange', _data => {
            const chainId = mapXverseNameToCaipNetwork(_data.stacks.name);
            this.emit('chainChanged', chainId);
        }));
    }
    unbindEvents() {
        this.walletUnsubscribes.forEach(unsubscribe => unsubscribe());
        this.walletUnsubscribes = [];
    }
}
//# sourceMappingURL=SatsConnectConnector.js.map